// Code generated by @hex-insights/frontend-generator; While this comment is present this file may be overwritten. If manual edits are made, remove this comment.
import {
	getArrayDiff,
	getObjectKeys,
	NullPartial,
	omitKeys,
	pickKeys,
	subsetTypeHasKey,
	ValueOf,
} from "@hex-insights/core";
import { EmployeeCreateInput, EmployeeDetailQuery, EmployeeUpdateInput } from "../../../Utilities";
import { PersonFormConversion } from "../Person";
import { TeacherCourseSectionEnrollmentFormConversion } from "../TeacherCourseSectionEnrollment";
import { TeacherHomeRoomSectionEnrollmentFormConversion } from "../TeacherHomeRoomSectionEnrollment";
import * as EmployeeFormValues from "./form-values";

/**
 * Converts the given `formValues` for the create form to a GraphQL create input for the Employee model.
 *
 * @param formValues The form values to convert.
 */
export function toGQLCreateInput<K extends keyof EmployeeFormValues.Create = keyof EmployeeFormValues.Create>(
	formValues: EmployeeFormValues.CreateSubset<K>,
): EmployeeCreateInput {
	const inputFormValues = omitKeys(formValues, [
		"person",
		"teacherCourseSectionEnrollments",
		"teacherHomeRoomSectionEnrollments",
	]);
	const input: NullPartial<EmployeeCreateInput> = {
		...inputFormValues,
		person:
			subsetTypeHasKey(formValues, "person") && formValues.person.length > 0
				? PersonFormConversion.toGQLCreateInEmployeeInput(formValues.person[0])
				: undefined,
		teacherCourseSectionEnrollments: subsetTypeHasKey(formValues, "teacherCourseSectionEnrollments")
			? formValues.teacherCourseSectionEnrollments.map((e) =>
					TeacherCourseSectionEnrollmentFormConversion.toGQLCreateInTeacherInput(e),
			  )
			: undefined,
		teacherHomeRoomSectionEnrollments: subsetTypeHasKey(formValues, "teacherHomeRoomSectionEnrollments")
			? formValues.teacherHomeRoomSectionEnrollments.map((e) =>
					TeacherHomeRoomSectionEnrollmentFormConversion.toGQLCreateInTeacherInput(e),
			  )
			: undefined,
	};
	return input as EmployeeCreateInput;
}

/**
 * Converts the given `formValues` for the detail form to a GraphQL update input for the Employee model.
 *
 * @param formValues The form values to convert. Generally only the changed form values.
 * @param initialFormValues The initial values of the form.
 */
export function toGQLUpdateInput<K extends keyof EmployeeFormValues.Detail = keyof EmployeeFormValues.Detail>(
	formValues: Partial<EmployeeFormValues.DetailSubset<K>>,
	initialFormValues: EmployeeFormValues.DetailSubset<K>,
): EmployeeUpdateInput {
	const inputFormValues = omitKeys(formValues, ["campusIDs", "classYearCategoryIDs", "classYearIDs"]);
	const input: EmployeeUpdateInput = { ...inputFormValues };

	if (subsetTypeHasKey(formValues, "endDate") && formValues.endDate === null) {
		input.clearEndDate = true;
	}

	if (
		subsetTypeHasKey(formValues, "campusIDs") &&
		subsetTypeHasKey(initialFormValues, "campusIDs") &&
		formValues.campusIDs !== undefined
	) {
		const { added, removed } = getArrayDiff(initialFormValues.campusIDs, formValues.campusIDs);
		input.addCampusIDs = added;
		input.removeCampusIDs = removed;
	}

	if (
		subsetTypeHasKey(formValues, "classYearCategoryIDs") &&
		subsetTypeHasKey(initialFormValues, "classYearCategoryIDs") &&
		formValues.classYearCategoryIDs !== undefined
	) {
		const { added, removed } = getArrayDiff(initialFormValues.classYearCategoryIDs, formValues.classYearCategoryIDs);
		input.addClassYearCategoryIDs = added;
		input.removeClassYearCategoryIDs = removed;
	}

	if (
		subsetTypeHasKey(formValues, "classYearIDs") &&
		subsetTypeHasKey(initialFormValues, "classYearIDs") &&
		formValues.classYearIDs !== undefined
	) {
		const { added, removed } = getArrayDiff(initialFormValues.classYearIDs, formValues.classYearIDs);
		input.addClassYearIDs = added;
		input.removeClassYearIDs = removed;
	}

	return input;
}

export type EmployeeFormKeyMap = {
	workEmailAddress: "workEmailAddress";
	jobTitle: "jobTitle";
	role: "role";
	startDate: "startDate";
	endDate: "endDate";
	bio: "bio";
	campusIDs: "campuses";
	classYearCategoryIDs: "classYearCategories";
	classYearIDs: "classYears";
	personID: "person";
};

export type ToFormValuesArgs<K extends keyof EmployeeFormValues.Detail = keyof EmployeeFormValues.Detail> = {
	employee: Pick<EmployeeDetailQuery["employee"], ValueOf<Pick<EmployeeFormKeyMap, K>>>;
	fields?: { [Key in K]: true };
};

/**
 * Converts a Employee instance to its corresponding detail form values.
 *
 * @param employee The instance to convert.
 * @param fields The subset of fields to include.
 */
export function toFormValues<K extends keyof EmployeeFormValues.Detail = keyof EmployeeFormValues.Detail>({
	employee,
	fields,
}: ToFormValuesArgs<K>): EmployeeFormValues.DetailSubset<K> {
	const formValues = {
		workEmailAddress: subsetTypeHasKey(employee, "workEmailAddress") ? employee.workEmailAddress : "",
		jobTitle: subsetTypeHasKey(employee, "jobTitle") ? employee.jobTitle : "",
		role: subsetTypeHasKey(employee, "role") ? employee.role : null,
		startDate: subsetTypeHasKey(employee, "startDate") ? employee.startDate : null,
		endDate: subsetTypeHasKey(employee, "endDate") ? employee.endDate : null,
		bio: subsetTypeHasKey(employee, "bio") ? employee.bio : "",
		campusIDs: subsetTypeHasKey(employee, "campuses") ? employee.campuses.map((e) => e.id) : [],
		classYearCategoryIDs: subsetTypeHasKey(employee, "classYearCategories")
			? employee.classYearCategories.map((e) => e.id)
			: [],
		classYearIDs: subsetTypeHasKey(employee, "classYears") ? employee.classYears.map((e) => e.id) : [],
		personID: subsetTypeHasKey(employee, "person") ? employee.person?.id ?? null : null,
	};
	return fields ? pickKeys(formValues, getObjectKeys(fields)) : formValues;
}
